import { 
  SearchParams,
  SearchType,
  ApiSearchParams,
  SortValue,
  BaseSearchParams
} from '../../types/search';
import { LiveSearchParams } from '../../types/lives';
import { 
  SEARCH_RESULT_LIMIT,
  SEARCH_OPTIONS,
  API_ENDPOINTS
} from '../../constants/search';
import { FILTER_DEFINITIONS } from '../../config/filter.config';

// Type guard to differentiate between SearchParams and LiveSearchParams
function isSearchParams(params: SearchParams | LiveSearchParams): params is SearchParams {
  return 'filters' in params;
}

// Validate and normalize base search parameters
function validateBaseParams(params: Partial<BaseSearchParams>): BaseSearchParams {
  return {
    term: params.term?.trim() || '',
    page: Math.max(1, params.page || 1),
    limit: Math.min(100, Math.max(1, params.limit || SEARCH_RESULT_LIMIT)),
    sortBy: params.sortBy,
    sortType: params.sortType
  };
}

// Validate search parameters based on their type
function validateSearchParams(params: SearchParams | LiveSearchParams): SearchParams | LiveSearchParams {
  const baseParams = validateBaseParams(params);

  if (isSearchParams(params)) {
    // Handle SearchParams
    return {
      ...baseParams,
      contentType: params.contentType,
      filters: {
        topics: params.filters.topics || [],
        duration: params.filters.duration,
        language: params.filters.language,
        status: params.filters.status,
        sortBy: params.filters.sortBy,
        sortType: params.filters.sortType,
        broadcastStatus: params.filters.broadcastStatus || []
      }
    };
  }

  // Handle LiveSearchParams
  return {
    ...baseParams,
    broadcastStatus: params.broadcastStatus
  };
}

// Get API configuration for a specific content type
function getApiConfigForContentType(contentType: SearchType) {
  const option = SEARCH_OPTIONS.find(opt => opt.id === contentType);
  if (!option) {
    throw new Error(`Invalid content type: ${contentType}`);
  }
  return option.apiConfig;
}

// Transform search parameters to API parameters
function transformToApiParams(params: SearchParams | LiveSearchParams): {
  endpoint: string;
  params: ApiSearchParams;
} {
  if (isSearchParams(params)) {
    const apiConfig = getApiConfigForContentType(params.contentType);
    const baseParams: ApiSearchParams = {
      page: params.page || 1,
      limit: params.limit || SEARCH_RESULT_LIMIT
    };

    if (params.term) {
      baseParams.term = params.term;
    }

    // Apply filter definitions
    Object.values(FILTER_DEFINITIONS).forEach(filterDef => {
      if (filterDef.isVisible(params.contentType)) {
        const apiParams = filterDef.getApiParam(params.filters);
        if (apiParams) {
          Object.assign(baseParams, apiParams);
        }
      }
    });

    return {
      endpoint: apiConfig.endpoint,
      params: {
        ...baseParams,
        ...apiConfig.params
      }
    };
  }

  // Handle LiveSearchParams
  return {
    endpoint: API_ENDPOINTS.LIVES,
    params: {
      ...params,
      page: params.page || 1,
      limit: params.limit || SEARCH_RESULT_LIMIT,
      broadcastStatus: params.broadcastStatus ? [params.broadcastStatus] : undefined
    }
  };
}

export function createSearchParams(params: SearchParams | LiveSearchParams): {
  endpoint: string;
  params: ApiSearchParams;
} {
  const validatedParams = validateSearchParams(params);
  return transformToApiParams(validatedParams);
}

export function createQueryString(params: ApiSearchParams): string {
  const urlParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value = value.filter(Boolean); // Remove empty values
        if (value.length > 0) {
          urlParams.append(key, value.join(','));
        }
      } else {
        urlParams.append(key, String(value));
      }
    }
  });

  const queryString = urlParams.toString();
  return queryString ? `?${queryString}` : '';
}

export function parseSearchParams(queryString: string): SearchParams {
  const urlParams = new URLSearchParams(queryString);
  const contentType = (urlParams.get('contentType') as SearchType) || 'video';
  
  return {
    term: urlParams.get('term') || '',
    contentType,
    page: Math.max(1, parseInt(urlParams.get('page') || '1', 10)),
    limit: Math.min(100, Math.max(1, parseInt(urlParams.get('limit') || SEARCH_RESULT_LIMIT.toString(), 10))),
    filters: {
      topics: urlParams.get('topics')?.split(',').filter(Boolean) || [],
      duration: urlParams.get('duration') || undefined,
      language: urlParams.get('language') || undefined,
      sortBy: (urlParams.get('sortBy') as SortValue) || undefined,
      sortType: urlParams.get('sortType') as 'asc' | 'desc' | undefined,
      broadcastStatus: urlParams.get('broadcastStatus')?.split(',').filter(Boolean) || []
    }
  };
} 